import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from "@angular/router";
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, Subject, BehaviorSubject, interval, defer, throwError, EMPTY } from 'rxjs';
import { map, switchMap, take, tap, retry, timeout, catchError, finalize, shareReplay, filter } from 'rxjs/operators';

import { LocalStoreManager } from './local-store-manager.service';
import { EndpointFactory } from './endpoint-factory.service';
import { AbstractEndpoint } from './abstract-endpoint.service';
import { ConfigurationService } from './configuration.service';
import { DBkeys } from './db-keys';
import { Utilities } from './utilities';
import { LoginResponse, IdToken } from '../models/login-response.model';
import { User } from '../models/user.model';
import { Permission, PermissionNames, PermissionValues } from '../models/permission.model';
import { Roles } from '../models/enums';
import { ODPProjectWorkflowViewModel } from '../models/odpprojectworkflow/odpProjectWorkflow.model';
import { TeamSelectionViewModel } from '../models/team/team.model';
import { Role } from '../models/role.model';

@Injectable({ providedIn: 'root'})
export class AuthService {

  public get loginUrl() { return this.configurations.loginUrl; }
  public get homeUrl() { return this.configurations.homeUrl; }

  public loginRedirectUrl: string;
  public logoutRedirectUrl: string;

  public reLoginDelegate: () => void;

  private previousIsLoggedInCheck = false;
  private _loginStatus = new Subject<boolean>();
  private _activeRole = new Subject<Roles>();
  private _activeProject = new Subject<ODPProjectWorkflowViewModel>();
  private _activeTeam = new Subject<TeamSelectionViewModel>();
  private _activeValsSelected = new Subject<boolean>();
  private _loginTimeout$ = new BehaviorSubject<number | null>(null);
  private _tokenTimeout$ = new BehaviorSubject<number | null>(null);
  private _sessionExpiresIn: number;
  private _tokenExpiresIn: number;
  private _refreshActiveObservable$: Observable<User> | null = null;
  private _broadcastChannel: BroadcastChannel | null = null;
  private _tabId: string = crypto.randomUUID();
  private _lastKnownExpiresAt: number | null = null;

  public updatingTokenInProgress = false;
  public isRestoringUserContextAfterRefresh = false;

  constructor(
    private router: Router,
    private configurations: ConfigurationService,
    private endpointFactory: EndpointFactory,
    private abstractendpoint: AbstractEndpoint,
    private localStorage: LocalStoreManager
  ) {
    this.initializeLoginStatus();
    this.initializeCrossTabSync();
  }

  private initializeLoginStatus() {
    console.log("syncTokenTimerFromStorage called from initializeLoginStatus to set up initial token timer");
    this.syncTokenTimerFromStorage();
    this.localStorage.getInitEvent().subscribe(() => {
      this.reevaluateLoginStatus();
      console.log("Storage initialized event received, syncing token timer from storage");
      this.syncTokenTimerFromStorage();
    });
  }

  // Read the stored auth token expiration and start the token timer so newly opened tabs pick up the countdown.
  private syncTokenTimerFromStorage() {
    console.log("syncTokenTimerFromStorage called to initialize token timer from stored expiry");
    try {
      const tokenIsoString = this.localStorage.getDataObject<string>(DBkeys.TOKEN_EXPIRES_IN, true);
      if (!tokenIsoString)
        return;

      const expiresAt = Date.parse(tokenIsoString);
      if (isNaN(expiresAt))
        return;

      if (this._lastKnownExpiresAt === expiresAt) {
        console.log('syncTokenTimerFromStorage: expiry unchanged, skipping reset');
        return;
      }
      this._lastKnownExpiresAt = expiresAt;

      const secondsLeft = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));

      if (secondsLeft > 0) {
        console.log('Initializing token timeout from storage:', secondsLeft, 'seconds');
        this.resetTokenTimeout(secondsLeft);
      }
      else if (secondsLeft <= 0) {
        console.log('Persisted access token already expired; redirecting to login.');
        this.redirectLogoutUser();
      }
    }
    catch (err) {
      console.warn('Failed to initialize token timer from storage', err);
    }
  }

  gotoPage(page: string, preserveParams = true) {

    let navigationExtras: NavigationExtras = {
      queryParamsHandling: preserveParams ? "merge" : "", preserveFragment: preserveParams
    };


    this.router.navigate([page], navigationExtras);
  }

  roleFromName(name: string): Roles {
    switch (name.toLowerCase()) {
      case Roles[Roles.Admin].toLowerCase(): return Roles.Admin;
      case Roles[Roles.ReconciliationSupervisor].toLowerCase(): return Roles.ReconciliationSupervisor;
      case Roles[Roles.ReconciliationCoder].toLowerCase(): return Roles.ReconciliationCoder;
      case Roles[Roles.QCSupervisor].toLowerCase(): return Roles.QCSupervisor;
      case Roles[Roles.QCCoder].toLowerCase(): return Roles.QCCoder;
      default: return null;
    }
  }

  private initializeCrossTabSync() {
    // BroadcastChannel as the primary mechanism for cross-tab communication, with localStorage events as a fallback for critical updates (ex. token expiry) to ensure tabs stay in sync even if BroadcastChannel is unavailable or messages are missed.
    try {
      console.log("Initializing BroadcastChannel for cross-tab auth sync");
      this._broadcastChannel = new BroadcastChannel('pact-auth');
      this._broadcastChannel.onmessage = (ev) => {
        const message = ev.data;
        if (!message) return;

        // Ignore broadcasts originating from this tab
        if (message.tab && message.tab === this._tabId) return;

        switch (message.type) {
          case 'refresh-start':
            console.log('Received refresh-start broadcast from another tab, setting updatingTokenInProgress to true');
            this.updatingTokenInProgress = true;
            break;
          case 'refresh-success':
            this.updatingTokenInProgress = false;
            this.reevaluateLoginStatus();

            try {
              const msgExpiresAt = typeof (message.expiresAt) === 'number' ? message.expiresAt : null;
              if (msgExpiresAt != null) {
                // Apply only when expiry changed
                if (this._lastKnownExpiresAt !== msgExpiresAt) {
                  this._lastKnownExpiresAt = msgExpiresAt;
                  const secondsLeft = Math.max(0, Math.floor((msgExpiresAt - Date.now()) / 1000));
                  console.log('Broadcast refresh-success includes expiresAt, resetting token timeout to', secondsLeft);
                  this.resetTokenTimeout(secondsLeft);
                }
                else {
                  console.log('Broadcast refresh-success: expiresAt unchanged, skipping reset');
                }
              }
              else {
                // Read stored auth token expiration if message did not include it
                const tokenIsoString = this.localStorage.getDataObject<string>(DBkeys.TOKEN_EXPIRES_IN, true);
                if (tokenIsoString) {
                  const expiresAt = Date.parse(tokenIsoString);
                  if (!isNaN(expiresAt) && this._lastKnownExpiresAt !== expiresAt) {
                    console.log('Broadcast refresh-success: read TOKEN_EXPIRES_IN from storage, resetting token timeout');
                    this._lastKnownExpiresAt = expiresAt;
                    const secondsLeft = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
                    this.resetTokenTimeout(secondsLeft);
                  }
                  else {
                    console.log('Broadcast refresh-success: TOKEN_EXPIRES_IN unchanged or invalid, skipping reset');
                  }
                }
                else {
                  console.log('Broadcast refresh-success: no expiresAt in message or storage');
                }
              }
            }
            catch (err) {
              console.warn('Error handling refresh-success', err);
            }
            break;
          case 'logout':
            this.redirectLogoutUser();
            break;
        }
      };
    }
    catch (e) {
      console.warn('BroadcastChannel not available', e);
    }

    window.addEventListener('storage', (e: StorageEvent) => {
      try {
        if (!e.key) return;

        // safeParse helper inline
        const safeParse = (s: string | null) => {
          if (!s)
            return null;
          try {
            return JSON.parse(s);
          }
          catch {
            return null;
          }
        };

        // PACT lock start/clear (detect stale locks)
        if (e.key === DBkeys.TOKEN_EXPIRES_IN) {
          console.log('Storage event: TOKEN_EXPIRES_IN changed, checking for updates');
          let tokenIsoString: string | null = null;
          try {
            const parsed = JSON.parse(e.newValue as string);
            tokenIsoString = typeof parsed === 'string' ? parsed : null;
          }
          catch {
            tokenIsoString = e.newValue as string;
          }

          if (tokenIsoString) {
            const expiresAt = Date.parse(tokenIsoString);
            if (!isNaN(expiresAt)) {
              if (this._lastKnownExpiresAt === expiresAt) {
                console.log('Storage: expiry unchanged, skipping reset from event key TOKEN_EXPIRES_IN');
              }
              else {
                this._lastKnownExpiresAt = expiresAt;
                const secondsLeft = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
                console.log('Storage event: TOKEN_EXPIRES_IN updated, resetting token timeout to', secondsLeft);
                this.reevaluateLoginStatus();
                this.resetTokenTimeout(secondsLeft);
              }
              return;
            }
          }

          this.reevaluateLoginStatus();
          console.log('TOKEN_EXPIRES_IN: no expiresAt found, skipping token timer reset');
          return;
        }

        if (e.key === 'pact:refresh_in_progress') {
          console.log('Storage event: pact:refresh_in_progress', e);
          const payload = safeParse(e.newValue);
          // Ignore events originating from this tab
          if (payload && payload.tab === this._tabId) return;

          const now = Date.now();
          const LOCK_STALE_REFRESH_MS = 60000;
          if (payload) {
            // If payload is stale, clear it and allow refresh attempts
            if (payload.timestamp && (now - payload.timestamp) > LOCK_STALE_REFRESH_MS) {
              try { localStorage.removeItem('pact:refresh_in_progress'); } catch { }
              this.updatingTokenInProgress = false;
              console.warn('Cleared stale pact:refresh_in_progress lock from other tab');
            }
            else {
              this.updatingTokenInProgress = true;
            }
          }
          else {
            // Lock was removed by another tab
            this.updatingTokenInProgress = false;
          }
          return;
        }

        if (e.key === 'pact:refresh_success') {
          console.log('Storage event: pact:refresh_success', e);
          const payload = safeParse(e.newValue);
          if (payload && payload.tab === this._tabId) return;
          this.updatingTokenInProgress = false;
          this.reevaluateLoginStatus();

          try {
            const payloadExpiresAt = typeof payload?.expiresAt === 'number' ? payload.expiresAt : null;
            if (payloadExpiresAt != null) {
              if (this._lastKnownExpiresAt !== payloadExpiresAt) {
                this._lastKnownExpiresAt = payloadExpiresAt;
                const secondsLeft = Math.max(0, Math.floor((payloadExpiresAt - Date.now()) / 1000));
                console.log('pact:refresh_success payload includes expiresAt, resetting token timeout to', secondsLeft);
                this.resetTokenTimeout(secondsLeft);
                return;
              }
              else {
                console.log('pact:refresh_success: expiresAt unchanged, skipping reset');
                return;
              }
            }
          }
          catch (err) {
            console.warn('Error reading pact:refresh_success payload', err);
          }

          // Fallback: try to read DBkeys.TOKEN_EXPIRES_IN
          try {
            const tokenIsoString = this.localStorage.getDataObject<string>(DBkeys.TOKEN_EXPIRES_IN, true);
            if (tokenIsoString) {
              const expiresAt = Date.parse(tokenIsoString);
              if (!isNaN(expiresAt) && this._lastKnownExpiresAt !== expiresAt) {
                this._lastKnownExpiresAt = expiresAt;
                const secondsLeft = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
                this.resetTokenTimeout(secondsLeft);
                return;
              }
            }
          }
          catch (err) {
            console.warn('Error reading TOKEN_EXPIRES_IN after refresh_success', err);
          }

          console.log('pact:refresh_success: no authoritative expiresAt found, skipping token timer reset');
          return;
        }
      }
      catch (e) {
        console.warn('Error handling storage event', e);
      }
    }, false);
  }

  // Centralized refresh that shares observable that is active during execution and broadcasts state across tabs.
  public refreshLoginOnce(): Observable < User > {
    if(this._refreshActiveObservable$) return this._refreshActiveObservable$;

    // Announce start to other tabs
    if(this._broadcastChannel) this._broadcastChannel.postMessage({ type: 'refresh-start', tab: this._tabId });
    try {
      localStorage.setItem('pact:refresh_in_progress', JSON.stringify({ tab: this._tabId, timestamp: Date.now() }));
    }
    catch (e) {
      console.warn('Could not set refresh_in_progress lock in localStorage', e);
    }

    this.updatingTokenInProgress = true;

    this._refreshActiveObservable$ = this.refreshLogin().pipe(
      retry(3),
      timeout(15000),
      tap(() => {
        console.log("refreshLoginOnce succeeded, broadcasting success to other tabs");
        // read stored auth token expiration
        let expiresAt: number | null = null;
        try {
          const tokenIso = this.localStorage.getDataObject<string>(DBkeys.TOKEN_EXPIRES_IN, true);
          if (tokenIso) {
            const parsed = Date.parse(tokenIso);
            if (!isNaN(parsed))
              expiresAt = parsed;
          }
        }
        catch (err) {
          console.warn('Could not read TOKEN_EXPIRES_IN to include expiresAt in success payload', err);
        }

        // First attempt: Post success to BroadcastChannel
        if (this._broadcastChannel) {
          this._broadcastChannel.postMessage({ type: 'refresh-success', tab: this._tabId, expiresAt });
        }

        // Fallback: Post to local storage 
        try {
          const payload: any = { tab: this._tabId, timestamp: Date.now() };
          if (expiresAt != null)
            payload.expiresAt = expiresAt;
          localStorage.setItem('pact:refresh_success', JSON.stringify(payload));
          setTimeout(() => {
            try {
              localStorage.removeItem('pact:refresh_success');
            }
            catch {
              console.log("Could not remove pact:refresh_success from localStorage");
            }
          }, 1000);
        }
        catch (e) {
          console.warn("Error on code to set pact:refresh_success in local storage.")
        }
      }),
      catchError(er => {
        console.error('refreshLoginOnce final failure', er);
        // broadcast failure -> cause other tabs to stop trying and to logout
        if (this._broadcastChannel) this._broadcastChannel.postMessage({ type: 'logout', tab: this._tabId });
        this.redirectLogoutUser(); // clears local storage & notifies others via storage event
        return EMPTY;
      }),
      finalize(() => {
        this.updatingTokenInProgress = false;
        this._refreshActiveObservable$ = null;
        // clear the lock marker
        try {
          localStorage.removeItem('pact:refresh_in_progress');
        }
        catch (e) {
          console.warn('Could not remove refresh_in_progress lock in localStorage', e);
        }
      }),
      // Allow other subscribers in same tab to reuse result
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this._refreshActiveObservable$;
  }

  redirectLoginUser() {
    let redirect = this.loginRedirectUrl && this.loginRedirectUrl != '/' && this.loginRedirectUrl != ConfigurationService.defaultHomeUrl ? this.loginRedirectUrl : this.homeUrl;
    this.loginRedirectUrl = null;


    let urlParamsAndFragment = Utilities.splitInTwo(redirect, '#');
    let urlAndParams = Utilities.splitInTwo(urlParamsAndFragment.firstPart, '?');

    let navigationExtras: NavigationExtras = {
      fragment: urlParamsAndFragment.secondPart,
      queryParams: Utilities.getQueryParamsFromString(urlAndParams.secondPart),
      queryParamsHandling: "merge"
    };

    // Override for multiple role selection
    var skipRoleSelect = false;
    //if (this.currentUser.activeRole == null || this.currentUser.activeTeam == null) {
    //  if (this.currentUser.roles.length > 1 || this.currentUser.teams.length > 1) {
    //    sendToRoleSelect = true;
    //  }

    //  if (this.currentUser.roles.length > 0 && this.currentUser.roles[0])
    //    this.setUserActiveRole(this.roleFromName(this.currentUser.roles[0]));

    //  if (this.currentUser.teams && this.currentUser.teams.length > 0)
    //    this.setUserActiveTeam(this.currentUser.teams[0]);
    //}
    //if (this.currentUser.activeRole != null && this.currentUser.activeProject != null && this.currentUser.activeTeam != null) 
    if (this.currentUser.activeRole != null && this.currentUser.activeProject != null && (this.currentUser.activeTeam != null ||
      ((this.currentUser.activeRole == Roles.QCSupervisor || this.currentUser.activeRole == Roles.ReconciliationSupervisor) && this.currentUser.activeTeam == null))) {
      skipRoleSelect = true;
    }
    if (skipRoleSelect)
      this.router.navigate([urlAndParams.firstPart], navigationExtras);
    //this.router.navigate(['/home/actions']);
    else {

      this.router.navigate(['/account/role/select']);
    }
  }


  redirectLogoutUser() {
    let redirect = this.logoutRedirectUrl ? this.logoutRedirectUrl : this.loginUrl;
    this.logoutRedirectUrl = null;
    this.resetTimeout(null, true);
    this.router.navigate([redirect]);
    console.log("Begin logout from redirectLogoutUser");
    this.logout();
  }


  redirectForLogin() {
    this.loginRedirectUrl = this.router.url;
    this.router.navigate([this.loginUrl]);
  }


  reLogin() {

    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
    this.localStorage.deleteData(DBkeys.WARNING_SHOWS_AT);

    if (this.reLoginDelegate) {
      this.reLoginDelegate();
    }
    else {
      this.redirectForLogin();
    }
  }


  refreshLogin() {
    console.log("Attempting to call refreshLogin to refresh token", (new Date().toISOString()) );
    return this.endpointFactory.getRefreshLoginEndpoint<LoginResponse>().pipe(
      map(response => this.processLoginResponse(response, this.rememberMe, true)));
  }


  // Below is a test function to test the retry functionality of the token refresh in the auth interceptor.
  // It simulates 3 failed refresh attempts followed by a successful one.
  /*private numberOfRefreshes = 0;

  refreshLoginTest() {
    // Use defer so each subscription (retry) executes this block again.
    return defer(() => {
      if (this.numberOfRefreshes < 3) {
        this.numberOfRefreshes++;
        console.log(`Simulating failed token refresh attempt number ${this.numberOfRefreshes}`);
        return throwError(() => new Error('Something went wrong'));
      } else {
        this.numberOfRefreshes = 0;
        console.log('Simulating successful token refresh on third attempt');
        return this.endpointFactory.getRefreshLoginEndpoint<LoginResponse>().pipe(
          map(response => this.processLoginResponse(response, this.rememberMe, true))
        );
      }
    });
  }*/

  //These functions are special for the auth interceptor and manual activity refresh because auth interceptor gets called on every server action.
  //We don't want to endlessly refresh the token as refresh itself is a server action.

  authInterceptorRefreshTokenOnAction(requestUrl: string) {
    if (!requestUrl.toLowerCase().includes(this.loginUrl.toLowerCase()) && !requestUrl.toLowerCase().includes(this.endpointFactory.loginUrl.toLowerCase())) {
      console.log("Starting authInterceptorRefreshTokenOnAction");
      var beforeRefreshRole: Roles = this.currentUser.activeRole;
      var beforeRefreshProj = this.currentUser.activeProject;
      var beforeRefreshTeam = this.currentUser.activeTeam;
      this.updatingTokenInProgress = true;
      console.log("Starting refresh login token from auth interceptor");
      this.refreshLoginOnce().subscribe(_ => {
        console.log("Refreshed login token from auth interceptor");
        this.setUserActiveRole(beforeRefreshRole, false);
        this.setUserActiveProject(beforeRefreshProj, false);
        this.setUserActiveTeam(beforeRefreshTeam, false);
        this.setUserActiveValsSelected(true, false);
        this.updatingTokenInProgress = false;
      });
    }
    else {
      console.log("The request URL signifies that the token does not need to be refreshed.", requestUrl);
    }
  }

  refreshTokenAndResetTimerManuallyOnAction() {
    console.log("Starting refreshTokenAndResetTimerOnAction");
    var beforeRefreshRole: Roles = this.currentUser.activeRole;
    var beforeRefreshProj = this.currentUser.activeProject;
    var beforeRefreshTeam = this.currentUser.activeTeam;
    this.updatingTokenInProgress = true;
    console.log("Starting refresh login token manually on action");
    this.refreshLoginOnce().subscribe(_ => {
      console.log("Refreshed login token manually on action");
      this.setUserActiveRole(beforeRefreshRole, false);
      this.setUserActiveProject(beforeRefreshProj, false);
      this.setUserActiveTeam(beforeRefreshTeam, false);
      this.setUserActiveValsSelected(true, false);
      this.updatingTokenInProgress = false;
    });

    this.resetTimeout();
    console.log("Reset timer manually on action");
  }

  login(userName: string, password: string, rememberMe?: boolean) {

    if (this.isLoggedIn) {
      console.log("Begin logout from login");
      this.logout();
    }

    return this.endpointFactory.getLoginEndpoint<LoginResponse>(userName, password).pipe(
      map(response => this.processLoginResponse(response, rememberMe)));
  }

  getUserTeams(userId: string, roleId: string, project: ODPProjectWorkflowViewModel) {
    var projectId = null;
    if (project)
      projectId = project.odpProjectWorkflowId.toString()
    console.log(`getUserTeams called with userid ${userId}, roleId ${roleId}, projectId ${projectId}`);
    return this.endpointFactory.getUserTeamsEndpoint<Array<TeamSelectionViewModel>>(userId, roleId, projectId).pipe(
      map(response => this.processUserTeamsResponse(response))
    );
  }

  private processUserTeamsResponse(teamIds: Array<TeamSelectionViewModel>) {
    this.setUserTeams(teamIds);
    console.log("teamIds ");
    console.log(teamIds);
  }

  getUserProjects(userId: string, roleId: string) {
    console.log(`getUserProjects called with id ${userId} role ${roleId} `);
    return this.abstractendpoint.getUserProjectsEndpoint<Array<ODPProjectWorkflowViewModel>>(userId, roleId).pipe(
      map(response => this.processUserProjectsResponse(response))
    );
  }

  private processUserProjectsResponse(projectIds: Array<ODPProjectWorkflowViewModel>) {
    this.setUserProjects(projectIds);
    console.log("projectIds ");
    console.log(projectIds);
  }

  private processLoginResponse(response: LoginResponse, rememberMe: boolean, isRefresh?: boolean) {

    let accessToken = response.access_token;

    if (accessToken == null)
      throw new Error("Received accessToken was empty");

    let idToken = response.id_token;
    let refreshToken = response.refresh_token || this.refreshToken;
    this._tokenExpiresIn = response.expires_in;
    this._sessionExpiresIn = this.configurations.idleTimeLogout / 1000;   

    let sessionExpiryDate = new Date();
    sessionExpiryDate.setSeconds(sessionExpiryDate.getSeconds() + this._sessionExpiresIn);
    // This is our default auto-logout warning time of 5 minutes
    let timeToWarnInSeconds = this.configurations.timeToIdleWarning / 1000;
    let warningShowsDate = new Date(sessionExpiryDate);
    warningShowsDate.setSeconds(warningShowsDate.getSeconds() - timeToWarnInSeconds);
    let sessionExpiry = sessionExpiryDate.toJSON();
    let warningShowsAt = warningShowsDate.toJSON();

    let tokenExpiryDate = new Date();
    tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + this._tokenExpiresIn);
    let accessTokenExpiry = tokenExpiryDate.toJSON();

    // Setting initial value so that the timer gets initialized with the correct expiry time on login and on refresh from other tabs.
    this._lastKnownExpiresAt = tokenExpiryDate.valueOf();

    const jwtHelper = new JwtHelperService();
    let decodedIdToken = <IdToken>jwtHelper.decodeToken(response.id_token);

    let permissions: PermissionValues[] = Array.isArray(decodedIdToken.permission) ? decodedIdToken.permission : [decodedIdToken.permission];

    if (!this.isLoggedIn)
      this.configurations.import(decodedIdToken.configuration);

    let user = new User(
      decodedIdToken.sub,
      decodedIdToken.name,
      decodedIdToken.fullname,
      decodedIdToken.firstname,
      decodedIdToken.lastname,
      decodedIdToken.email,
      decodedIdToken.jobtitle,
      decodedIdToken.phone,
      Array.isArray(decodedIdToken.role) ? decodedIdToken.role : [decodedIdToken.role]);
    user.isEnabled = true;

    if (!isRefresh) {
      user.activeRole = null;
      user.activeProject = null;
      user.activeTeam = null;
      this.resetTimeout();
    }
    
    this.resetTokenTimeout();
    this.saveUserDetails(user, permissions, accessToken, idToken, refreshToken, accessTokenExpiry, sessionExpiry, warningShowsAt, rememberMe, isRefresh);
    this.reevaluateLoginStatus(user);

    if (user.activeRole != null) {
      // get user projects
      this.getUserProjects(decodedIdToken.sub, user.activeRole).subscribe(result => {
        console.log(result);
      }, error => {
        console.log("error on getUserProjects");
      });
    }

    // get user teams here
    if (user.activeRole != null && user.activeProject != null) {
      this.getUserTeams(decodedIdToken.sub, user.activeRole, user.activeProject).subscribe(result => {
        console.log(result);
      }, error => {
        console.log("error on getUserTeams");
      });
    }

    return user;
  }

  private saveUserDetails(user: User, permissions: PermissionValues[], accessToken: string, idToken: string, refreshToken: string, tokenExpiresIn: string, sessionExpiresIn: string, warningShowsAt: string, rememberMe: boolean, isRefresh?: boolean) {

    if (rememberMe) {
      this.localStorage.savePermanentData(accessToken, DBkeys.ACCESS_TOKEN);
      this.localStorage.savePermanentData(idToken, DBkeys.ID_TOKEN);
      this.localStorage.savePermanentData(refreshToken, DBkeys.REFRESH_TOKEN);
      this.localStorage.savePermanentData(tokenExpiresIn, DBkeys.TOKEN_EXPIRES_IN);
      if (!isRefresh) {
        this.localStorage.savePermanentData(sessionExpiresIn, DBkeys.SESSION_EXPIRES_IN);
        this.localStorage.savePermanentData(warningShowsAt, DBkeys.WARNING_SHOWS_AT);
      }
      this.localStorage.savePermanentData(permissions, DBkeys.USER_PERMISSIONS);
      this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
    }
    else {
      this.localStorage.saveSyncedSessionData(accessToken, DBkeys.ACCESS_TOKEN);
      this.localStorage.saveSyncedSessionData(idToken, DBkeys.ID_TOKEN);
      this.localStorage.saveSyncedSessionData(refreshToken, DBkeys.REFRESH_TOKEN);
      this.localStorage.saveSyncedSessionData(tokenExpiresIn, DBkeys.TOKEN_EXPIRES_IN);
      if (!isRefresh) {
        this.localStorage.saveSyncedSessionData(sessionExpiresIn, DBkeys.SESSION_EXPIRES_IN);
        this.localStorage.saveSyncedSessionData(warningShowsAt, DBkeys.WARNING_SHOWS_AT);
      }
      this.localStorage.saveSyncedSessionData(permissions, DBkeys.USER_PERMISSIONS);
      this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
    }

    this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
  }



  logout(): void {
    console.log(`BEGIN LOGOUT`);
    this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
    this.localStorage.deleteData(DBkeys.ID_TOKEN);
    this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);
    this.localStorage.deleteData(DBkeys.SESSION_EXPIRES_IN);
    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
    this.localStorage.deleteData(DBkeys.WARNING_SHOWS_AT);
    this.localStorage.deleteData(DBkeys.USER_PERMISSIONS);
    this.localStorage.deleteData(DBkeys.CURRENT_USER);

    this.configurations.clearLocalChanges();

    this.reevaluateLoginStatus();
  }


  private reevaluateLoginStatus(currentUser?: User) {

    let user = currentUser || this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    let isLoggedIn = user != null;

    if (this.previousIsLoggedInCheck != isLoggedIn) {
      setTimeout(() => {
        this._loginStatus.next(isLoggedIn);
      });
    }

    this.previousIsLoggedInCheck = isLoggedIn;
  }


  getLoginStatusEvent(): Observable<boolean> {
    return this._loginStatus.asObservable();
  }

  getActiveRoleChangeEvent(): Observable<Roles> {
    return this._activeRole.asObservable();
  }

  getActiveProjectChangeEvent(): Observable<ODPProjectWorkflowViewModel> {
    return this._activeProject.asObservable();
  }

  getActiveTeamChangeEvent(): Observable<TeamSelectionViewModel> {
    return this._activeTeam.asObservable();
  }

  getActiveValuesSelectedChangeEvent(): Observable<boolean> {
    return this._activeValsSelected.asObservable();
  }

  getTimeBeforeIdleWarning(){
    // Getting these values from the environment file
    let idleTime = this.configurations.idleTimeLogout / 1000;
    let warnTime = this.configurations.timeToIdleWarning / 1000;

    // Dividing by 60 to get minutes
    let diff = (idleTime - warnTime) / 60;
    return diff;
  }

  setUserActiveRole(role: Roles, timeout: boolean = true) {
    //console.log("Calling authService.setUserActiveRole");
    let user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    user.activeRole = role;
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
    if (timeout) {
      setTimeout(() => {
        this._activeRole.next(role);
      });
    }
  }

  setUserActiveProject(project: ODPProjectWorkflowViewModel, timeout: boolean = true) {
    //console.log("Calling authService.setUserActiveProject");
    let user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    user.activeProject = project;
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);

    if (timeout) {
      setTimeout(() => {
        this._activeProject.next(project);
      });
    }
  }

  setUserActiveTeam(team: TeamSelectionViewModel, timeout: boolean = true) {
    //console.log("Calling authService.setUserActiveTeam");
    let user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    user.activeTeam = team;
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);

    if (timeout) {
      setTimeout(() => {
        this._activeTeam.next(team);
      });
    }
  }

  setUserActiveValsSelected(valsselected: boolean, timeout: boolean = true) {
    //console.log("Calling authService.setUserActiveValsSelected");
    let user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    user.activeValsSelected = valsselected;
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);

    if (timeout) {
      setTimeout(() => {
        this._activeValsSelected.next(valsselected);
      });
    }
  }

  setUserRoles(roles: string[]) {
    const user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    user.roles = roles;
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
    setTimeout(() => this._activeRole.next(user.activeRole));
  }

  setUserProjects(projects: ODPProjectWorkflowViewModel[]) {
    const user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    user.projects = projects;
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
    setTimeout(() => this._activeProject.next(user.activeProject));
  }

  setUserTeams(teams: TeamSelectionViewModel[]) {
    const user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    user.teams = teams;
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
    setTimeout(() => this._activeTeam.next(user.activeTeam));
  }

  setUserValsSelected(valsselected: boolean) {
    const user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    user.activeValsSelected = valsselected;
    this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
    setTimeout(() => this._activeValsSelected.next(user.activeValsSelected));
  }

  // Reset timeout using either passed time or the access token expiration time.
  resetTimeout(timeout?: number, logout?: boolean) {
    this._sessionExpiresIn = this.configurations.idleTimeLogout / 1000;

    let sessionExpiryDate = new Date();
    sessionExpiryDate.setSeconds(sessionExpiryDate.getSeconds() + this._sessionExpiresIn);
    // This is our default auto-logout warning time of 5 minutes
    let timeToWarnInSeconds = this.configurations.timeToIdleWarning / 1000;
    let warningShowsDate = new Date(sessionExpiryDate);
    warningShowsDate.setSeconds(warningShowsDate.getSeconds() - timeToWarnInSeconds);
    let sessionExpiry = sessionExpiryDate.toJSON();
    let warningShowsAt = warningShowsDate.toJSON();

    this._loginTimeout$.next(timeout ? timeout : this._sessionExpiresIn);

    if (!logout) {
      this.saveTimeoutDetails(sessionExpiry, warningShowsAt, false);
    }
  }

  private saveTimeoutDetails(sessionExpiresIn: string, warningShowsAt: string, rememberMe: boolean)
  { 
    if (rememberMe) {
      this.localStorage.savePermanentData(sessionExpiresIn, DBkeys.SESSION_EXPIRES_IN);
      this.localStorage.savePermanentData(warningShowsAt, DBkeys.WARNING_SHOWS_AT);
    }
    else {
      this.localStorage.saveSyncedSessionData(sessionExpiresIn, DBkeys.SESSION_EXPIRES_IN);
      this.localStorage.saveSyncedSessionData(warningShowsAt, DBkeys.WARNING_SHOWS_AT);
    }

    this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
  }

  // Returns an observable that emits the time left in seconds until the user is logged out due to inactivity.
  // Logs out the user when the time reaches 0.
  getInactivityTimer(callback: () => any) {
    return this._loginTimeout$.pipe(
      filter(t => t != null),
      switchMap(t => interval(1000).pipe(
        map(i => (t as number) - i),
        take((t as number) + 1),
        tap(count => {
          if (!count && this.isLoggedIn) {
            callback();
          }
        })
      )));
  }

  // Reset timeout using either passed time or the access token expiration time.
  resetTokenTimeout(timeout?: number) {
    this._tokenTimeout$.next(timeout ? timeout : this._tokenExpiresIn);
  }

  // Returns an observable that emits the time left in seconds until the user is logged out due to inactivity.
  // Logs out the user when the time reaches 0.
  getTokenExpiryTimer(callback: () => any) {
    return this._tokenTimeout$.pipe(
      filter(t => t != null),
      switchMap(t => interval(1000).pipe(
        map(i => (t as number) - i),
        take((t as number) + 1),
        tap(count => {
          //console.log(`Token expiry countdown: ${count}`);
          if (!count) {
            if (this.isLoggedIn) {
              console.log('Token expiry reached and user is logged in, invoking callback.');
              callback();
            } else {
              console.log('Token expiry reached but user is not logged in, skipping callback.');
            }
          }
        })
      )));
  }

  get currentUser(): User {

    let user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    this.reevaluateLoginStatus(user);

    return user;
  }

  get userPermissions(): PermissionValues[] {
    return this.localStorage.getDataObject<PermissionValues[]>(DBkeys.USER_PERMISSIONS) || [];
  }

  get accessToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.ACCESS_TOKEN);
  }

  get loginExpiryDate(): Date {

    this.reevaluateLoginStatus();
    return this.localStorage.getDataObject<Date>(DBkeys.SESSION_EXPIRES_IN, true);
  }

  get accessTokenExpiryDate(): Date {

    this.reevaluateLoginStatus();
    return this.localStorage.getDataObject<Date>(DBkeys.TOKEN_EXPIRES_IN, true);
  }

  get accessShowWarningDate(): Date {

    this.reevaluateLoginStatus();
    return this.localStorage.getDataObject<Date>(DBkeys.WARNING_SHOWS_AT, true);
  }

  get isSessionExpired(): boolean {

    if (this.loginExpiryDate == null) {
      return true;
    }

    return !(this.loginExpiryDate.valueOf() > new Date().valueOf());
  }

  get isTokenExpired(): boolean {

    if (this.accessTokenExpiryDate == null) {
      return true;
    }

    return !(this.accessTokenExpiryDate.valueOf() > new Date().valueOf());
  }

  get shouldWarningShow(): boolean {
    if (this.accessShowWarningDate == null) {
      return false;
    }

    return !(this.accessShowWarningDate.valueOf() > new Date().valueOf()) && this.isLoggedIn;
  }


  get idToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.ID_TOKEN);
  }

  get refreshToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.REFRESH_TOKEN);
  }

  get isLoggedIn(): boolean {
    return this.currentUser != null;
  }

  get rememberMe(): boolean {
    return this.localStorage.getDataObject<boolean>(DBkeys.REMEMBER_ME) == true;
  }
}
